/**
 * MIT License
 *
 * Copyright (c) 2023–Present PPResume (https://ppresume.com)
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to
 * deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or
 * sell copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS
 * IN THE SOFTWARE.
 */
import { z } from 'zod'

import { joinNonEmptyString } from '@/utils'
import {
  NetworkOptionSchema,
  SizedStringSchema,
  UrlSchema,
} from '../primitives'
import { nullifySchema } from '../utils'

/**
 * A zod schema for a username.
 */
export const UsernameSchema = SizedStringSchema('username', 2, 64).meta({
  title: 'Username',
  description: 'Your username or handle on the social network.',
  examples: ['john_doe', 'jane.smith', 'dev_engineer', 'designer_123'],
})

/**
 * A zod schema for a profile item.
 */
export const ProfileItemSchema = z.object({
  // required fields
  network: NetworkOptionSchema,
  username: UsernameSchema,

  // optional fields
  url: nullifySchema(UrlSchema),
})

/**
 * A zod schema for profiles.
 */
export const ProfilesSchema = z.object({
  profiles: z
    .array(ProfileItemSchema)
    .nullish()
    .meta({
      title: 'Profiles',
      description: joinNonEmptyString(
        [
          'The profiles section contains your online presence,',
          'including social media and professional network profiles.',
        ],
        ' '
      ),
    }),
})
